import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';
import { formatInteger, FormattedValueWithUnit } from '../../common/formatHelper';
import useGetNamedColor from '../../common/useGetNamedColor';
import { cn } from '../../common/utils';
import { ChartType, WarehouseKPI } from '../../dashboard/kpi/kpiModel';
import ChartTooltip from '../../simulation/panels/ChartTooltip';
import ContainerChart from '../../simulation/panels/ContainerChart';
import ButtonSwitchMulti from '../actions/ButtonSwitchMulti';
import * as Icon from '../icons';
import { StatCenterPie, StatCenterPieProps } from '../stats/StatCenterPie';

// Supported chart types for this component

const colors = TwTheme.extend.colors;

export type ChartDataItem = {
  id: string;
  title?: string; // For ResponsiveBar compatibility
  value: number;
  label: string;
  color: string[];
  formatted: FormattedValueWithUnit;
  originalData?: any; // Optional reference to original data for context
};

// Utility function to convert PieData to ChartDataItem
export const convertPieDataToChartDataItem = (
  pieData: any[],
): ChartDataItem[] => {
  return pieData.map(item => ({
    id: String(item.id),
    value: item.value,
    label: item.label,
    color: Array.isArray(item.color) ? item.color : [item.color, item.color],
    formatted: item.formatted,
  }));
};

export type GenericMetricChartProps = {
  data: ChartDataItem[];
  metricDescriptor?: WarehouseKPI;
  showChartTypeSelector?: boolean;
  defaultChartType?: ChartType;
  forcedChartType?: ChartType; // Force a specific chart type, overrides all other logic
  onChartTypeChange?: (chartType: ChartType) => void;
  className?: string;
  containerHeight?: number;
  innerRadius?: number;

  // Chart customization options
  showAxisLabels?: boolean;
  showUnitOfMeasure?: boolean;
  showCenterPie?: boolean;
  centerPieProps?: StatCenterPieProps;
  centerPieStat?: React.ReactNode;

  // Total calculation
  totalValue?: number;
  totalFormattedValue?: string;
};

const GenericMetricChart: React.FC<GenericMetricChartProps> = ({
  data,
  metricDescriptor,
  showChartTypeSelector = true,
  defaultChartType = 'pie',
  forcedChartType,
  onChartTypeChange,
  className,
  containerHeight = 250,
  innerRadius = 0.65,
  showAxisLabels = true,
  showUnitOfMeasure = true,
  showCenterPie = false,
  centerPieProps,
  centerPieStat,
  totalValue: providedTotalValue,
  totalFormattedValue: providedTotalFormattedValue,
}) => {
  const { t } = useTranslation('app');
  const getNamedColor = useGetNamedColor();

  // Determine available chart types from metricDescriptor or fallback to default
  const getAvailableChartTypes = (): ChartType[] => {
    if (metricDescriptor?.recommendedVisualization?.chartType) {
      return metricDescriptor.recommendedVisualization.chartType.filter(
        (type): type is ChartType =>
          [
            'bar',
            'pie',
            'line',
            'groupped-bar',
            'stacked-bar',
            'table',
            'kpi',
            'stat',
          ].includes(type),
      );
    }
    return ['bar', 'pie']; // Allow both types if no recommendations
  };

  const getInitialChartType = (): ChartType => {
    // 1. Use forced chart type if provided
    if (forcedChartType) {
      return forcedChartType;
    }

    // 2. Use first recommended visualization type if available
    if (metricDescriptor?.recommendedVisualization?.chartType) {
      const recommendedTypes =
        metricDescriptor.recommendedVisualization.chartType.filter(
          (type): type is ChartType =>
            [
              'bar',
              'pie',
              'line',
              'groupped-bar',
              'stacked-bar',
              'table',
              'kpi',
              'stat',
            ].includes(type),
        );
      if (recommendedTypes.length > 0) {
        return recommendedTypes[0];
      }
    }

    // 3. Use default chart type if it's available in recommendations
    const availableChartTypes = getAvailableChartTypes();
    if (defaultChartType && availableChartTypes.includes(defaultChartType)) {
      return defaultChartType;
    }

    // 4. Fallback to first available type or pie
    return availableChartTypes[0] || 'pie';
  };

  const availableChartTypes = getAvailableChartTypes();
  const initialChartType = getInitialChartType();

  const [chartType, setChartType] = useState<ChartType>(initialChartType);

  // Use forced chart type if provided, otherwise use state
  const activeChartType = forcedChartType || chartType;

  const handleChartTypeChange = (newChartType: ChartType) => {
    setChartType(newChartType);
    onChartTypeChange?.(newChartType);
  };

  // Calculate chart dimensions and properties
  const chartItemsCount = data.length;
  const showTicks = chartItemsCount < 10;
  const rotateTicks = chartItemsCount > 5;

  const maxTextLength = Math.max(...data.map(item => item.label.length), 10);

  const baseBottomMargin = 40;
  const additionalMarginPerChar = 4;
  const minBottomMargin = 50;
  const maxBottomMargin = 150;

  const bottomMargin = rotateTicks
    ? Math.min(
        Math.max(
          baseBottomMargin + maxTextLength * additionalMarginPerChar,
          minBottomMargin,
        ),
        maxBottomMargin,
      )
    : minBottomMargin;

  const chartHeight = containerHeight;

  // Chart data transformation
  const chartData = data.map(item => ({
    ...item, // Spread original item first
    id: item.id,
    title: item.title,
    label: item.label,
    value: item.value,
    formattedValue: item.formatted.value, // Override with consistent formatting
    color: item.color, // Keep the full color array
  }));

  // Calculate total for center pie display
  const calculatedTotalValue = _.sumBy(data, 'value');
  const totalValue = providedTotalValue ?? calculatedTotalValue;

  // Use provided formatted value or calculate a reasonable default
  const getTotalFormattedValue = () => {
    if (providedTotalFormattedValue) {
      return formatInteger(providedTotalFormattedValue);
    }
    console.log('getTotalFormattedValue', providedTotalFormattedValue);

    if (data.length === 0) return formatInteger(totalValue);

    // Try to use the same formatting as the first item
    const firstItem = data[0];
    if (firstItem.formatted?.value) {
      // For now, return the raw total value
      // The parent component should provide the properly formatted total
      return formatInteger(totalValue);
    }

    return formatInteger(totalValue);
  };

  // Default center pie props if not provided
  const defaultCenterPieProps: StatCenterPieProps = {
    statLabelMiddle: 'Total',
    statValueMiddle: getTotalFormattedValue(),
  };

  // Merge provided props with defaults
  const finalCenterPieProps = centerPieProps
    ? { ...defaultCenterPieProps, ...centerPieProps }
    : defaultCenterPieProps;

  // Consistent color and formatting functions
  const getColor = (data: any) => {
    // Use primary color from array consistently across all chart types
    if (Array.isArray(data.color)) {
      return data.color[0] || colors.chart.value;
    }
    return data.color || colors.chart.value;
  };

  const getPieColor = (row: any) => {
    // The row.data should contain our chartData item
    const dataItem = row.data;
    if (dataItem && Array.isArray(dataItem.color) && dataItem.color[0]) {
      return dataItem.color[0];
    }

    // Fallback: generate color based on index using getNamedColor
    const index = chartData.findIndex(item => item.id === row.id);
    if (index !== -1) {
      const fallbackColor = getNamedColor(String(row.id))[0];
      return fallbackColor;
    }

    return colors.chart.value;
  };

  const getFormattedValue = (d: any) => {
    // Use consistent formatting across all chart types
    // Handle cases where formatted values are objects
    if (d.formattedValue) {
      if (typeof d.formattedValue === 'object' && d.formattedValue.value) {
        return d.formattedValue.value;
      }
      return d.formattedValue;
    }

    if (d.formatted?.value) {
      if (typeof d.formatted.value === 'object' && d.formatted.value.value) {
        return d.formatted.value.value;
      }
      return d.formatted.value;
    }

    return d.value.toString();
  };

  const getTooltipColor = (d: any): [string, string] => {
    // Use full color array for tooltips (primary + secondary)
    if (Array.isArray(d.color)) {
      return [
        d.color[0] || colors.chart.value,
        d.color[1] || d.color[0] || colors.chart.value,
      ];
    }

    // Fallback to getNamedColor for consistent color generation
    if (d.color) {
      return [d.color, d.color];
    }

    const fallbackColor = getNamedColor(
      String(d.id || d.title || 'default'),
    )[0];
    return [fallbackColor, fallbackColor];
  };

  const getUnitOfMeasure = (d: any) => {
    if (!showUnitOfMeasure) return undefined;

    // Check formattedValue object first
    if (typeof d.formattedValue === 'object' && d.formattedValue?.unit) {
      return d.formattedValue.unit;
    }

    // Check formatted object
    if (typeof d.formatted === 'object' && d.formatted?.unit) {
      return d.formatted.unit;
    }

    // Check if formatted.value is an object with unit
    if (typeof d.formatted?.value === 'object' && d.formatted.value?.unit) {
      return d.formatted.value.unit;
    }

    return undefined;
  };

  if (!data.length) {
    return null;
  }

  console.log(
    // `GenericMetricChart::chartData:: ${metricDescriptor?.title}/ ${metricDescriptor?.legend}`,
    chartData,
    metricDescriptor,
  );

  return (
    <div
      data-chart-type={activeChartType}
      data-component="generic-metric-chart"
      className={cn('mb-2', className)}
    >
      {/* Chart Type Selector */}
      {showChartTypeSelector &&
        !forcedChartType &&
        availableChartTypes.length > 1 && (
          <div className="flex items-center justify-end p-2">
            <ButtonSwitchMulti
              buttonType="minimal"
              className="flex-1"
              options={availableChartTypes.map(type => {
                const iconMap = {
                  bar: (
                    <div className={cn('flex w-full items-center')}>
                      <Icon.ViewGraph
                        className={cn(
                          'fill-current',
                          'group-hover:opacity-50',
                          'h-4 w-full',
                        )}
                      />
                    </div>
                  ),
                  pie: (
                    <div className={cn('flex w-full items-center')}>
                      <Icon.DataStatsPie
                        className={cn(
                          'fill-current',
                          'group-hover:opacity-50',
                          'h-4 w-full',
                        )}
                      />
                    </div>
                  ),
                  line: (
                    <div className={cn('flex w-full items-center')}>
                      <Icon.ViewGraph
                        className={cn(
                          'fill-current',
                          'group-hover:opacity-50',
                          'h-4 w-full',
                        )}
                      />
                    </div>
                  ),
                  'groupped-bar': (
                    <div className={cn('flex w-full items-center')}>
                      <Icon.ViewGraph
                        className={cn(
                          'fill-current',
                          'group-hover:opacity-50',
                          'h-4 w-full',
                        )}
                      />
                    </div>
                  ),
                  'stacked-bar': (
                    <div className={cn('flex w-full items-center')}>
                      <Icon.ViewGraph
                        className={cn(
                          'fill-current',
                          'group-hover:opacity-50',
                          'h-4 w-full',
                        )}
                      />
                    </div>
                  ),
                  table: (
                    <div className={cn('flex w-full items-center')}>
                      <span
                        className={cn(
                          'text-xs font-bold',
                          'group-hover:opacity-50',
                        )}
                      >
                        T
                      </span>
                    </div>
                  ),
                  kpi: (
                    <div className={cn('flex w-full items-center')}>
                      <span
                        className={cn(
                          'text-xs font-bold',
                          'group-hover:opacity-50',
                        )}
                      >
                        K
                      </span>
                    </div>
                  ),
                  stat: (
                    <div className={cn('flex w-full items-center')}>
                      <span
                        className={cn(
                          'text-xs font-bold',
                          'group-hover:opacity-50',
                        )}
                      >
                        S
                      </span>
                    </div>
                  ),
                };

                return {
                  label: type, // Add label for proper key generation
                  icon: iconMap[type] || (
                    <div className={cn('flex w-full items-center')}>
                      <span
                        className={cn(
                          'text-xs font-bold',
                          'group-hover:opacity-50',
                        )}
                      >
                        {type.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  ),
                };
              })}
              selectedIndex={availableChartTypes.indexOf(activeChartType)}
              onClick={index => {
                const selectedType = availableChartTypes[index];
                if (selectedType) {
                  handleChartTypeChange(selectedType);
                }
              }}
              autoSize
            />
          </div>
        )}

      {/* Chart Container */}
      <ContainerChart className="relative">
        {activeChartType === 'bar' ||
        activeChartType === 'groupped-bar' ||
        activeChartType === 'stacked-bar' ? (
          <ResponsiveBar
            data={chartData.map(item => ({
              id: item.id,
              title: item.title || item.label,
              value: item.value, // Use raw numeric value, not formatted string
              formattedValue: item.formatted.value, // Keep formatted for display
              color: getColor(item),
            }))}
            keys={['value']}
            indexBy="title"
            margin={{ top: 5, right: 2, bottom: bottomMargin, left: 2 }}
            padding={
              chartData.length > 20 ? 0.02 : chartData.length > 10 ? 0.05 : 0.1
            }
            colors={({ data }) => getColor(data)}
            borderRadius={1}
            borderWidth={0.5}
            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
            // borderRadius={2}
            axisBottom={
              showAxisLabels && showTicks
                ? {
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: rotateTicks ? -90 : 0,
                  }
                : null
            }
            axisLeft={null}
            enableGridX={false}
            enableGridY={true}
            enableLabel={true}
            label={datum => {
              // datum should have formattedValue from our data mapping
              if (datum.data && datum.data.formattedValue) {
                return datum.data.formattedValue;
              }
              const originalItem = chartData.find(item => item.id === datum.id);
              return (
                originalItem?.formatted?.value ||
                getFormattedValue(originalItem || datum)
              );
            }}
            labelSkipWidth={20}
            labelSkipHeight={8}
            labelTextColor={({ data }) => {
              return getBestContrastColor(getColor(data));
            }}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: colors.chart.ticks,
                    fontSize: 10,
                  },
                },
              },
              grid: {
                line: {
                  stroke: colors.chart.grid,
                },
              },
              labels: {
                text: {
                  fontSize: 9,
                  fontWeight: 500,
                },
              },
            }}
            tooltip={({ data }) => {
              // Find the original data item for formatted values
              const metricValue = chartData.find(item => item.id === data.id);
              const formattedValue =
                data.formattedValue || metricValue?.formatted?.value;

              return (
                <ChartTooltip
                  color={getTooltipColor(data)}
                  title={data?.title}
                  value={getFormattedValue(data)}
                  unitOfMeasure={getUnitOfMeasure(data)}
                />
              );
            }}
          />
        ) : activeChartType === 'pie' ? (
          <>
            <ResponsivePie
              data={chartData}
              value="value"
              id="title"
              fit={true}
              animate={false} // TODO: fix animation jumps
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={showCenterPie ? innerRadius || 0.7 : 0.5}
              // padAngle={1}
              // cornerRadius={1.5}
              // colors={({ data }) => getColor(data)}
              colors={({ data }) => getColor(data)}
              // colors={getPieColor} // Use the special pie color function
              // borderWidth={1}
              // borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              enableArcLinkLabels={false}
              // arcLinkLabelsTextColor={`black`}
              // arcLinkLabelsThickness={2}
              arcLinkLabelsColor={({ data }) => getColor(data)}
              arcLinkLabelsSkipAngle={10}
              arcLabelsSkipAngle={10}
              activeOuterRadiusOffset={8}
              borderWidth={0}
              startAngle={showCenterPie ? -90 : 0}
              arcLabel={({ data }) => data.formattedValue}
              theme={{
                labels: {
                  text: {
                    fontSize: 13,
                    fill: getBestContrastColor(getColor(data)),
                  },
                },
              }}
              tooltip={({ datum: { data } }) => (
                <ChartTooltip
                  color={getTooltipColor(data)}
                  title={data.title || data.label}
                  value={getFormattedValue(data)}
                  unitOfMeasure={getUnitOfMeasure(data)}
                />
              )}
            />

            {/* Center Pie Display */}
            {showCenterPie && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                {centerPieStat ? (
                  centerPieStat
                ) : (
                  <StatCenterPie {...finalCenterPieProps} />
                )}
              </div>
            )}
          </>
        ) : activeChartType === 'table' ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="text-menu-text text-sm">
                {t`Cannot Display current visualisation`}
              </div>
            </div>
          </div>
        ) : activeChartType === 'kpi' ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="text-menu-text text-sm">
                {t`Cannot Display current visualisation`}
              </div>
            </div>
          </div>
        ) : activeChartType === 'stat' ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="text-menu-text text-sm">
                {t`Cannot Display current visualisation`}
              </div>
            </div>
          </div>
        ) : activeChartType === 'stacked-bar-percent' ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="text-menu-text text-sm">
                {t`Cannot Display current visualisation`}
              </div>
            </div>
          </div>
        ) : activeChartType === 'gauge' ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="text-menu-text text-sm">
                {t`Cannot Display current visualisation`}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="text-menu-text text-sm">{activeChartType}</div>
            </div>
          </div>
        )}
      </ContainerChart>
    </div>
  );
};

export default GenericMetricChart;
